import {
    Column,
    GroupProps,
    NumberCol,
    sortStringInner,
    StringCol,
    Table,
    TID,
    useFilteredTableData,
    useTableRefresher,
    ViewOnly
} from "../../../../misc/scroller/Table";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {editFx, ProductItem, productRef} from "./transferProductRows";
import {TransferChangeFx} from "./useTransferEdits";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useAlternative} from "../alternative/Alternative";
import {CheckboxObjProvider} from "../../shopdrawing/keying/CheckboxObj";
import {ProductCol} from "../../shopdrawing/div10/Div10Table";
import {Product} from "../../../../api/Products";
import {beforeGroupClass} from "../../shopdrawing/keying/Keying";
import {amber, blue, grey, red} from "@mui/material/colors";
import {CheckColumn} from "./CheckColumn";
import {RowOptionCol} from "../../../logistics/purchasing/RowOptions";
import {reviewStatusPendingColor} from "../../shopdrawing/logistics/ReleaseColumns";
import {SupplierProvider, useSupplierCategories} from "../../shopdrawing/hardware/SupplierContext";
import {ProjectSupplierCategory} from "../../../../api/SupplierCategories";
import {Button, MenuItem, MenuList, Tooltip, Typography} from "@mui/material";
import {ArrowDropDown, CheckBox, CheckBoxOutlineBlank, Replay} from "@mui/icons-material";
import {Popover} from "../../../../misc/Popover";
import {SimpleIconButton} from "../../../../misc/lists/SimpleIconButton";
import {ProductEditFromId} from "../../shopdrawing/extras/ProductEdit";
import {css} from "@emotion/css";

const productLinkClass = css({
    cursor: "pointer",
    "&:hover": {textDecoration: "underline"},
});

export function TransferProductWorkTable(props: {
    value: ProductItem[];
    onChange: (rows: ProductItem[], fx: TransferChangeFx) => boolean;
    onProductSaved: (product: Product) => void;
    conflictKeys?: Set<string>;
    allowSplit?: boolean;
    div10?: boolean;
    ribbonContainer?: HTMLElement | null;
    ribbonStart?: React.ReactNode;
}) {
    return (
        <SupplierProvider>
            <TransferProductWorkTableInner {...props} />
        </SupplierProvider>
    );
}

function TransferProductWorkTableInner(props: {
    value: ProductItem[];
    onChange: (rows: ProductItem[], fx: TransferChangeFx) => boolean;
    onProductSaved: (product: Product) => void;
    conflictKeys?: Set<string>;
    allowSplit?: boolean;
    div10?: boolean;
    ribbonContainer?: HTMLElement | null;
    ribbonStart?: React.ReactNode;
}) {

    const project = useProjectId();
    const alternative = useAlternative();
    const valueRef = useRef(props.value);
    valueRef.current = props.value;

    // the table edits its rows in place before onChange, so it gets copies and
    // onRowChange can diff the edited copy against the unchanged row
    const fetch = useCallback(async () => valueRef.current.map(r => Object.assign({}, r)), []);
    const onChangeRef = useRef(props.onChange);
    onChangeRef.current = props.onChange;
    const supplierCategories = useSupplierCategories();
    const suppliersRef = useRef(supplierCategories.list);
    suppliersRef.current = supplierCategories.list;

    const tableName = `edit-product-${project}-${alternative}`;
    const filtered = useFilteredTableData<ProductItem>(tableName);
    const refreshTable = useTableRefresher(tableName);
    const [editProduct, setEditProduct] = useState<number>();

    useEffect(() => {
        refreshTable();
    }, [props.value, refreshTable]);

    const apply = (rows: ProductItem[], fx: TransferChangeFx) => {
        if(rows.length === 0) return;

        const ok = onChangeRef.current(rows, fx);
        if(!ok) {
            // put the rejected cell value back
            refreshTable();
        }
    };

    const currentRows = (list: ProductItem[]) => {
        const ids = new Set(list.map(f => f.id));
        return valueRef.current.filter(v => ids.has(v.id));
    };

    const onRowChange = (next: ProductItem) => {
        const row = valueRef.current.find(r => r.id === next.id);
        if(!row) return;

        if(next.remove !== row.remove) {
            apply([row], editFx.setRemove(next.remove));
        } else if((next.replaceWithProduct?.id || 0) !== (row.replaceWithProduct?.id || 0)) {
            apply([row], editFx.setReplace(next.replaceWithProduct || null));
        } else if(next.bySupplierCategory !== row.bySupplierCategory) {
            apply([row], editFx.setSupplier(next.bySupplierCategory, next.bySupplierCategoryName));
        }
    };

    const mainRows = (list: ProductItem[]) => list.filter(r => r.splitIndex === 0);
    const filteredMain = mainRows(currentRows(filtered));
    const allRemoved = filteredMain.length > 0 && filteredMain.every(f => f.remove);

    const ribbon = (
        <BulkActionRibbon
            start={props.ribbonStart}
            count={filtered.length}
            showSupplier={!props.div10}
            options={supplierCategories.list}
            allRemoved={allRemoved}
            onAssignSupplier={cat => apply(currentRows(filtered), editFx.setSupplier(cat.id, cat.name))}
            onToggleRemove={() => apply(mainRows(currentRows(filtered)), editFx.setRemove(!allRemoved))}
        />
    );

    return (
        <CheckboxObjProvider>
            {props.ribbonContainer ? createPortal(ribbon, props.ribbonContainer) : ribbon}
            <Table<ProductItem>
                name={tableName}
                globalPrefsName={"edit-product"}
                fetch={fetch}
                groupBy={{
                    groupFx: (input: ProductItem) => input.manufacturerName,
                    hasBeforeGroup: () => true,
                    beforeGroup: (props: GroupProps<ProductItem>) => {
                        return (
                            <div className={beforeGroupClass}>
                                {props.name}
                            </div>
                        )
                    },
                    groupSorter: sortStringInner,
                }}
                cellCustomize={{
                    backgroundColor: (input: ProductItem, column: Column<any>) => {
                        if(props.conflictKeys?.has(input.key)) return red["100"];
                        if(input.changedVia) return amber["100"];
                        if(input.bySupplierCategory !== input.bySupplierCategoryOriginal) return blue["100"];
                        if(input.replaceWithProduct && input.product !== input.replaceWithProduct?.id) return blue["100"];
                        if(input.splitProduct) return grey["100"];
                        if(input.reviewStatus === "pending") return reviewStatusPendingColor;
                        return "transparent";
                    }
                }}
                columns={[
                    RowOptionCol({
                        options: (dt: ProductItem) => {
                            return [{
                                name: "Edit current product",
                                onClick: async (ctrl) => {
                                    ctrl.onClose();
                                    setEditProduct(dt.product);
                                },
                            },
                            !!dt.replaceWithProduct && !dt.remove && {
                                name: "Edit replace product",
                                onClick: async (ctrl) => {
                                    ctrl.onClose();
                                    setEditProduct(dt.replaceWithProduct?.id);
                                },
                            },
                            props.allowSplit !== false && {
                                name: "Split",
                                onClick: async (ctrl) => {
                                    ctrl.onClose();
                                    apply(currentRows([dt]), editFx.addSplit());
                                },
                            },
                            dt.splitIndex > 0 && {
                                name: "Remove",
                                onClick: async (ctrl) => {
                                    ctrl.onClose();
                                    apply(currentRows([dt]), editFx.removeSplit());
                                },
                                danger: true,
                            }]
                        },
                    }),
                    {
                        name: "Reset",
                        renderName: () => "",
                        width: 35,
                        render: (dt: ProductItem) => (
                            <ResetRowButton
                                item={dt}
                                onReset={() => apply(currentRows([dt]), dt.splitIndex > 0 ? editFx.removeSplit() : editFx.reset())}
                            />
                        ),
                    } as Column<ProductItem>,
                    Object.assign(ViewOnly(StringCol("Current Name", "name", 300)), {
                        render: (dt: ProductItem) => (
                            <span className={productLinkClass} onClick={() => setEditProduct(dt.product)}>
                                {dt.name}
                            </span>
                        ),
                        copy: (dt: ProductItem) => dt.name,
                    }),
                    {
                        name: "Code",
                        width: 150,
                        render: (dt: ProductItem) => (
                            <Tooltip title={dt.changedVia ? `Changed via ${dt.changedVia}` : ""}>
                                <span>{dt.productCode}</span>
                            </Tooltip>
                        ),
                        copy: (dt: ProductItem) => dt.productCode,
                        filter: (dt: ProductItem, search: string) => dt.productCode.toLowerCase().indexOf(search.toLowerCase()) !== -1,
                        sort: (a: ProductItem, b: ProductItem) => sortStringInner(a.productCode, b.productCode),
                    } as Column<ProductItem>,
                    ViewOnly(StringCol("Finish", "finish")),
                    ViewOnly(NumberCol("Qty", "qty")),
                    ProductCol2({
                        name: "Replace With",
                        width: 300,
                        defaultValue: "Keep the same",
                        onRender: (p: ProductItem) => {
                            if(p.mixed) return "Mixed";
                            if(p.remove) return "Remove";
                            if(!p.replaceWithProduct) return "Keep the same"
                            return formatName(p.replaceWithProduct, true);
                        },
                        onSet: (p: ProductItem, product: Product) => {
                            if(p.remove) return;

                            p.replaceWithProduct = productRef(product);
                        },
                        onClear: (p: ProductItem) => {
                            p.replaceWithProduct = undefined;
                        },
                    }),
                    {
                        name: "Remove",
                        width: 120,
                        render: (d) => {
                            if(d.splitIndex > 0) return null;

                            return (
                                <CheckColumn
                                    value={d}
                                    getValue={(d: ProductItem) => {
                                        if(d.mixed) return null;
                                        return d.remove;
                                    }}
                                    onChange={(d: ProductItem, value: boolean) => {
                                        d.remove = value;
                                        d.replaceWithProduct = undefined;
                                    }}
                                />
                            );
                        }
                    } as Column<ProductItem>,
                    !props.div10 && {
                        name: "Supplier",
                        width: 200,
                        render: (d: ProductItem) => d.bySupplierCategoryName,
                        editKey: "bySupplierCategory",
                        editable: {
                            type: "select",
                            displayKey: "bySupplierCategoryName",
                            options: () => (suppliersRef.current || [])
                                .filter(s => !s.archived)
                                .map(s => ({display: s.name, value: String(s.id)})),
                            copy: (d: ProductItem) => d.bySupplierCategoryName || "",
                            paste: (d: ProductItem, value: string) => {
                                const name = value.trim();
                                if(!name) {
                                    d.bySupplierCategory = 0;
                                    d.bySupplierCategoryName = "";
                                    return;
                                }

                                const found = (suppliersRef.current || [])
                                    .find(s => s.name.toLowerCase() === name.toLowerCase());
                                if(!found) return;

                                d.bySupplierCategory = found.id;
                                d.bySupplierCategoryName = found.name;
                            },
                        },
                        editObj: (dst: ProductItem, _src: ProductItem, value: string) => {
                            const id = parseInt(value);
                            if(!id || isNaN(id)) {
                                dst.bySupplierCategory = 0;
                                dst.bySupplierCategoryName = "";
                                return;
                            }
                            const found = (suppliersRef.current || []).find(s => s.id === id);
                            dst.bySupplierCategory = id;
                            dst.bySupplierCategoryName = found?.name || "";
                        },
                        filter: (d: ProductItem, search: string) => {
                            return (d.bySupplierCategoryName || "").toLowerCase().indexOf(search.toLowerCase()) !== -1;
                        },
                        sort: (a: ProductItem, b: ProductItem) =>
                            sortStringInner(a.bySupplierCategoryName || "", b.bySupplierCategoryName || ""),
                    } as Column<ProductItem>,
                ]}
                columnDeps={[
                    props.div10,
                    props.allowSplit,
                    props.conflictKeys,
                    supplierCategories.list,
                ]}
                fetchDeps={[project]}
                onChange={async (v) => {
                    onRowChange(v);
                }}
            />
            {editProduct && <ProductEditFromId id={editProduct} onClose={value => {
                setEditProduct(undefined);
                if(value) props.onProductSaved(value);
            }} />}
        </CheckboxObjProvider>
    )
}

function isRowModified(item: ProductItem) {
    if(item.splitProduct) return true;
    if(item.mixed) return true;
    if(item.remove) return true;
    if(item.bySupplierCategory !== item.bySupplierCategoryOriginal) return true;
    if(item.replaceWithProduct && item.product !== item.replaceWithProduct.id) return true;
    return false;
}

function ResetRowButton(props: {
    item: ProductItem;
    onReset: () => void;
}) {
    if(!isRowModified(props.item)) return null;

    return (
        <div onClick={e => e.stopPropagation()} style={{display: "flex", alignItems: "center"}}>
            <SimpleIconButton
                tooltip="Clear changes"
                onClick={props.onReset}>
                <Replay />
            </SimpleIconButton>
        </div>
    );
}

function BulkActionRibbon(props: {
    start?: React.ReactNode;
    count: number;
    showSupplier: boolean;
    options: ProjectSupplierCategory[] | null;
    allRemoved: boolean;
    onAssignSupplier(cat: ProjectSupplierCategory): void;
    onToggleRemove(): void;
}) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const opts = (props.options || []).filter(s => !s.archived);

    return (
        <div style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
        }}>
            {props.start}
            <Typography variant="caption" color="textSecondary">
                Filter table before doing bulk assignment
            </Typography>
            {props.showSupplier && <Button
                size="small"
                variant="outlined"
                disabled={props.count === 0}
                endIcon={<ArrowDropDown />}
                onClick={e => setAnchor(e.currentTarget)}
            >
                Assign Supplier to {props.count}
            </Button>}
            <Button
                size="small"
                variant="outlined"
                disabled={props.count === 0}
                startIcon={props.allRemoved ? <CheckBox /> : <CheckBoxOutlineBlank />}
                onClick={props.onToggleRemove}
            >
                Remove {props.count}
            </Button>
            {anchor && <Popover
                anchorEl={anchor}
                open
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                onClose={() => setAnchor(null)}>
                <MenuList dense>
                    {opts.map(o => (
                        <MenuItem key={o.id} onClick={() => {
                            props.onAssignSupplier(o);
                            setAnchor(null);
                        }}>{o.name}</MenuItem>
                    ))}
                    {opts.length === 0 && <MenuItem disabled>No categories</MenuItem>}
                </MenuList>
            </Popover>}
        </div>
    )
}

function ProductCol2<T extends TID>(props: {
    name: string;
    width: number;
    defaultValue?: string;
    onRender: (value: T) => string
    onSet(value: T, product: Product): void;
    onClear(value: T): void;
}): Column<T> {
    const props2 = props;

    return {
        name: props.name,
        width: 250,
        render: (data: T, col: any) => {
            return (
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <div>
                        {props.onRender(data)}
                    </div>
                </div>
            );
        },
        editObj: (dst, src, value: string) => {
            if(value === "") {
                props.onClear(dst);
            }
        },
        editable: {
            type: "custom",
            copy: data => {
                return ""
            },
            paste: (data, value) => {

            },
            render: (props) =>
                <ProductCol
                    kind="hardware"
                    width={props.width}
                    anchor={props.anchor}
                    onDone={async (v: {product: number; productObj?: Product}[]) => {
                        const obj = v[0].productObj;
                        if(!obj) return;

                        const obj2 = Object.assign({}, props.row);
                        props2.onSet(obj2, obj);
                        return props.onDone(obj2);
                    }}
                    onCancel={props.onCancel}
                    initialValue={""}
                />,
        },
        filter: (data, search) => {
            return props.onRender(data).toLowerCase().startsWith(search.toLowerCase())
        },
        sort: (a, b) => {
            return sortStringInner(props.onRender(a), props.onRender(b))
        }
    }
}

function formatName(p: ProductItem["replaceWithProduct"], mfg: boolean = false) {
    if(!p) return "";

    const finish = p.finish ? ` (${p.finish})` : "";
    const manufacturer = mfg ? `${p.manufacturerName} ` : "";
    return `${manufacturer}${p.name} - ${p.productCode}${finish}`
}
